import { ArrowRight, Network, Shield, Zap, Globe2, Server, Lock, TrendingUp, CheckCircle2, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import SEOMetadata from '@/components/SEOMetadata';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const NexxInnovate = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "NEXX TELECOM: Pioneering Innovation in Enterprise Connectivity and Residential Proxy Infrastructure",
    "description": "Comprehensive guide to NEXX TELECOM's innovative approach to enterprise connectivity, residential proxies, custom ISP solutions, and large-scale IP infrastructure management.",
    "author": {
      "@type": "Organization",
      "name": "NEXX TELECOM"
    },
    "publisher": {
      "@type": "Organization",
      "name": "NEXX TELECOM",
      "logo": {
        "@type": "ImageObject",
        "url": "https://nexx.net/logo.png"
      }
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01"
  };

  const innovations = [
    {
      icon: Network,
      title: "Custom Connectivity Architecture",
      description: "Bespoke network solutions designed for enterprise-scale operations, AI infrastructure, and high-performance computing environments."
    },
    {
      icon: Globe2,
      title: "Global Residential Proxy Network",
      description: "Premium residential IP infrastructure with rotating addresses from authentic residential networks across multiple countries."
    },
    {
      icon: Server,
      title: "Large IP Block Management",
      description: "Strategic allocation and management of IPv4 and IPv6 address spaces with ARIN justification expertise."
    },
    {
      icon: Shield,
      title: "ISP Partnership Program",
      description: "Direct relationships with Tier 1 carriers and regional ISPs for optimal routing and redundancy."
    }
  ];

  const technicalCapabilities = [
    {
      category: "Residential Proxy Infrastructure",
      items: [
        "Rotating residential IP addresses from real ISP networks",
        "Geographic targeting across 195+ countries",
        "Session management and sticky IP support",
        "Unlimited bandwidth options for enterprise clients",
        "99.9% uptime SLA with automatic failover",
        "SOCKS5 and HTTP/HTTPS protocol support",
        "Custom authentication and whitelisting",
        "API integration for automated proxy management"
      ]
    },
    {
      category: "Custom Connectivity Solutions",
      items: [
        "Dedicated fiber optic circuits from 100Mbps to 100Gbps",
        "BGP routing with ASN allocation and management",
        "Multi-homed redundant connectivity architecture",
        "Direct peering arrangements with major networks",
        "DDoS mitigation at the network edge",
        "MPLS and SD-WAN implementation",
        "Cross-connect services in major data centers",
        "Custom SLA agreements with penalty clauses"
      ]
    },
    {
      category: "IP Address Block Services",
      items: [
        "IPv4 /24 to /16 block allocation",
        "IPv6 /48 and larger allocations",
        "ARIN justification and documentation support",
        "IP reputation management and monitoring",
        "Reverse DNS delegation and management",
        "SWIP and reassignment handling",
        "Clean IP blocks with no spam history",
        "Portable IP addresses for multi-site deployment"
      ]
    },
    {
      category: "ISP & Carrier Relations",
      items: [
        "Direct partnerships with Tier 1 carriers (Cogent, GTT, Lumen)",
        "Regional ISP integration for last-mile connectivity",
        "Carrier-neutral colocation facilities",
        "Letter of Authorization (LOA) management",
        "Circuit provisioning and installation coordination",
        "Network Operations Center (NOC) support 24/7/365",
        "Bandwidth burst capabilities during traffic spikes",
        "Latency-optimized routing for specific applications"
      ]
    }
  ];

  const useCases = [
    {
      title: "AI & Machine Learning Operations",
      description: "High-bandwidth, low-latency connectivity for training large language models, computer vision systems, and distributed AI workloads requiring massive data throughput."
    },
    {
      title: "Web Scraping & Data Intelligence",
      description: "Residential proxy infrastructure enabling large-scale data collection from public sources while maintaining compliance and avoiding detection."
    },
    {
      title: "Ad Verification & Brand Protection",
      description: "Geo-distributed residential IPs for verifying ad placement, detecting click fraud, and monitoring brand presence across global markets."
    },
    {
      title: "Market Research & Competitive Analysis",
      description: "Anonymous data gathering from e-commerce platforms, search engines, and competitor websites using authentic residential connections."
    },
    {
      title: "SEO Monitoring & Rank Tracking",
      description: "Accurate search engine results page (SERP) tracking from different geographic locations using clean residential IP addresses."
    },
    {
      title: "Social Media Management",
      description: "Multi-account management for digital marketing agencies requiring residential IPs to avoid platform restrictions and maintain account health."
    },
    {
      title: "Enterprise Network Connectivity",
      description: "Campus-wide fiber deployments, multi-site MPLS networks, and redundant internet connectivity for mission-critical business operations."
    },
    {
      title: "Colocation & Data Center Services",
      description: "Carrier-neutral connectivity, cross-connects to major networks, and dedicated circuits for hosting providers and enterprise clients."
    }
  ];

  const competitiveAdvantages = [
    "No middleman markup - Direct carrier relationships mean better pricing",
    "Custom SLAs - Tailored service level agreements with real penalties for downtime",
    "Technical transparency - Full network diagrams and routing information provided",
    "Flexible contracts - Month-to-month options available, no forced long-term commitments",
    "In-house expertise - Network engineers, not sales reps, design your solution",
    "IP justification support - ARIN documentation and approval assistance included",
    "Carrier-agnostic approach - We work with any provider that meets your needs",
    "24/7 NOC support - Real engineers answering phones, not outsourced call centers"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMetadata
        title="NEXX TELECOM Innovation: Enterprise Connectivity, Residential Proxies & Custom IP Infrastructure"
        description="Discover NEXX TELECOM's innovative approach to enterprise connectivity, residential proxy networks, custom ISP solutions, and large-scale IP infrastructure. Expert-level technical solutions for AI operations, data intelligence, and mission-critical connectivity."
        keywords="NEXX TELECOM, residential proxies, enterprise connectivity, IP blocks, ISP solutions, fiber optic networks, BGP routing, data center connectivity, AI infrastructure, proxy network, IPv4 allocation, carrier services, network engineering, custom connectivity, ARIN IP justification"
        canonical="https://www.nexxinnovate.com"
        structuredData={structuredData}
      />
      
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-nexx-green/5 via-transparent to-nexx-dark/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-5xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-nexx-dark via-nexx-green to-nexx-dark bg-clip-text text-transparent leading-tight">
              NEXX TELECOM: Redefining Infrastructure Innovation
            </h1>
            <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8">
              Where enterprise connectivity meets residential proxy excellence. We engineer custom network solutions that power AI operations, data intelligence platforms, and mission-critical business infrastructure across the globe.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => window.location.href = 'https://nexx.net'}
                className="bg-nexx-green hover:bg-nexx-green/90 text-white px-8 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300"
              >
                Explore NEXX Solutions
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => window.location.href = 'tel:+13054901669'}
                className="border-2 border-nexx-green text-nexx-green hover:bg-nexx-green/10 px-8 py-6 text-lg font-bold"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call: +1 (305) 490-1669
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {innovations.map((innovation, index) => (
              <Card key={index} className="border-2 border-nexx-green/20 hover:border-nexx-green/40 transition-all duration-300 hover:shadow-xl">
                <CardHeader>
                  <div className="w-14 h-14 bg-nexx-green/10 rounded-xl flex items-center justify-center mb-4">
                    <innovation.icon className="h-7 w-7 text-nexx-green" />
                  </div>
                  <CardTitle className="text-xl">{innovation.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{innovation.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comprehensive Technical Capabilities */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
              Technical Excellence Across Every Layer
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From residential proxy networks to enterprise fiber deployments, NEXX TELECOM delivers infrastructure solutions built on decades of combined network engineering expertise.
            </p>
          </div>

          <div className="space-y-12">
            {technicalCapabilities.map((capability, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-nexx-green/40 transition-all duration-300">
                <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-6 flex items-center">
                  <Zap className="h-8 w-8 text-nexx-green mr-3" />
                  {capability.category}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {capability.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-nexx-green mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-nexx-dark to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black mb-6">
              Powering Innovation Across Industries
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From AI startups to Fortune 500 enterprises, NEXX TELECOM infrastructure enables the most demanding technical workloads.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300 rounded-lg p-6">
                <h3 className="text-xl font-bold text-white mb-4">{useCase.title}</h3>
                <p className="text-gray-300">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-6">
              Why Forward-Thinking Companies Choose NEXX
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not a traditional telecom provider. We're infrastructure engineers who happen to sell connectivity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {competitiveAdvantages.map((advantage, index) => (
              <div key={index} className="flex items-start bg-gradient-to-br from-nexx-green/5 to-transparent p-6 rounded-xl border border-nexx-green/20 hover:border-nexx-green/40 transition-all duration-300">
                <TrendingUp className="h-6 w-6 text-nexx-green mr-4 mt-1 flex-shrink-0" />
                <p className="text-gray-700 font-medium">{advantage}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deep Dive: Residential Proxy Infrastructure */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-8">
              Residential Proxy Infrastructure: The NEXX Difference
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-xl leading-relaxed">
                NEXX TELECOM operates one of the most sophisticated residential proxy networks in the industry. Unlike consumer-grade proxy services, our infrastructure is purpose-built for enterprise data intelligence operations requiring scale, reliability, and compliance.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Network Architecture & IP Sourcing</h3>
              <p>
                Our residential proxy network consists of IP addresses sourced exclusively from legitimate Internet Service Providers (ISPs) across 195+ countries. Every IP address in our network originates from real residential connections, ensuring authenticity that datacenter proxies cannot replicate. We maintain direct relationships with ISPs and implement strict ethical sourcing standards, meaning all IP addresses in our network come from users who have explicitly consented to participate in our network.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Rotating vs. Sticky Sessions</h3>
              <p>
                NEXX provides both rotating and sticky session options. Rotating proxies automatically change IP addresses with each request or at specified intervals, ideal for high-volume web scraping operations that require avoiding rate limits. Sticky sessions maintain the same IP address for extended periods (from minutes to hours), perfect for applications requiring session persistence like social media management or complex multi-step workflows.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Geographic Targeting Capabilities</h3>
              <p>
                Our infrastructure supports country-level, city-level, and even ISP-level targeting. This granular control enables precise geo-targeting for market research, ad verification, and localized content testing. Clients can specify exact locations to ensure their data collection reflects the true user experience in specific markets.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Protocol Support & Authentication</h3>
              <p>
                NEXX residential proxies support both HTTP/HTTPS and SOCKS5 protocols, providing flexibility for different applications. Authentication can be configured via username/password or IP whitelisting, with support for sub-user management and API-based credential rotation for enhanced security.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Bandwidth & Performance</h3>
              <p>
                Unlike providers who oversell bandwidth and impose hidden throttling, NEXX offers genuine unlimited bandwidth options for enterprise clients. Our network maintains sub-second connection times and supports concurrent connections scaling into the thousands. Advanced load balancing ensures optimal performance even during peak usage periods.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Compliance & Ethical Standards</h3>
              <p>
                NEXX TELECOM maintains the highest standards of ethical proxy operations. All IP addresses are sourced with full consent, and we implement comprehensive abuse prevention systems. Our network includes built-in compliance features to help clients adhere to data protection regulations including GDPR, CCPA, and industry-specific requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Dive: Custom Connectivity & ISP Relations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-8">
              Custom Connectivity & ISP Partnerships
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-xl leading-relaxed">
                NEXX TELECOM's connectivity solutions go far beyond standard internet service. We design, deploy, and manage custom network architectures that meet the specific requirements of AI operations, high-frequency trading platforms, content delivery networks, and enterprise multi-site deployments.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Direct Carrier Relationships</h3>
              <p>
                Through established partnerships with Tier 1 carriers including Cogent Communications, GTT (formerly Level 3), Lumen Technologies (CenturyLink), Zayo Group, and regional fiber providers, NEXX can provision connectivity in virtually any major market. These direct relationships eliminate broker markups and provide access to preferential pricing structures typically reserved for multi-million dollar contracts.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">BGP Routing & ASN Management</h3>
              <p>
                For clients requiring advanced routing control, NEXX provides BGP (Border Gateway Protocol) implementation with ASN (Autonomous System Number) allocation and management. This enables true multi-homed connectivity with automatic failover, traffic engineering for optimal routing, and the ability to advertise your own IP blocks across multiple carriers. Our network engineers handle the complexity of BGP configuration, prefix filtering, and route optimization.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Fiber Optic Circuit Provisioning</h3>
              <p>
                From 100Mbps to 100Gbps, NEXX manages the entire lifecycle of fiber optic circuit deployment. This includes site surveys, carrier selection and negotiation, Letter of Authorization (LOA) processing, installation coordination, testing and acceptance, and ongoing monitoring. We handle the complexity so you can focus on your business, not telecom logistics.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Data Center Connectivity & Cross-Connects</h3>
              <p>
                NEXX maintains presence in major colocation facilities including Equinix, Digital Realty, Cyxtera, and regional data centers. We can provision cross-connects to major cloud providers (AWS Direct Connect, Azure ExpressRoute, Google Cloud Interconnect), internet exchanges (IXPs), and other network operators. This connectivity fabric enables low-latency, high-bandwidth connections critical for distributed applications and hybrid cloud architectures.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">DDoS Mitigation & Network Security</h3>
              <p>
                Our connectivity solutions include enterprise-grade DDoS mitigation at the network edge. Using a combination of traffic scrubbing centers, anycast routing, and intelligent filtering, we can absorb and mitigate attacks before they impact your infrastructure. Additional security features include managed firewalls, intrusion detection/prevention systems (IDS/IPS), and 24/7 security operations center (SOC) monitoring.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">SLA Design & Performance Guarantees</h3>
              <p>
                NEXX works with clients to design custom Service Level Agreements (SLAs) that actually mean something. Our SLAs include real financial penalties for missed uptime targets, latency guarantees with continuous monitoring, defined mean time to repair (MTTR) commitments, and escalation procedures with direct access to senior engineers. We don't hide behind generic terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Deep Dive: IP Address Block Management */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-8">
              Large IP Block Allocation & Management
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-xl leading-relaxed">
                In an era where IPv4 addresses are a scarce commodity, NEXX TELECOM provides strategic guidance and implementation services for organizations requiring large IP address allocations. Our expertise spans ARIN policy compliance, IP reputation management, and efficient address space utilization.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">IPv4 Allocation in a Post-Exhaustion World</h3>
              <p>
                With ARIN's (American Registry for Internet Numbers) IPv4 free pool exhausted, obtaining new IPv4 addresses requires either transfers from existing holders or justified allocations from waiting lists. NEXX navigates this complex landscape on behalf of clients, handling ARIN documentation, justification narratives demonstrating immediate need and efficient utilization, transfer facilitation with IP brokers when necessary, and legal documentation including RSA (Registration Services Agreement) amendments.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">IPv6 Implementation & Strategy</h3>
              <p>
                While IPv4 scarcity drives prices higher, IPv6 offers abundant address space. NEXX provides comprehensive IPv6 implementation services including architecture design for dual-stack networks, /48 and larger allocations for enterprise use, DNS and reverse DNS configuration, and application compatibility testing. We help organizations transition to IPv6 while maintaining IPv4 compatibility during the migration period.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">IP Reputation Management</h3>
              <p>
                For organizations running email servers, proxy networks, or any service where IP reputation matters, NEXX provides clean IP blocks with documented history. Our IP reputation services include pre-allocation blacklist checking across major RBLs, continuous monitoring of IP reputation scores, delisting support if addresses are erroneously blocked, and proactive abuse prevention systems to maintain pristine reputation.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">SWIP and rDNS Management</h3>
              <p>
                Proper IP block management requires attention to administrative details. NEXX handles SWIP (Shared Whois Project) reassignments for IP blocks, reverse DNS delegation and PTR record management, WHOIS database accuracy and updates, and abuse contact configuration and monitoring. These seemingly minor details are critical for email deliverability, regulatory compliance, and professional network operations.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Portable IP Addresses</h3>
              <p>
                For organizations requiring provider independence, NEXX facilitates portable IP address allocations that can be announced across multiple carriers. This provider-agnostic approach enables true multi-homing, seamless provider transitions without renumbering, and leverage in carrier negotiations. Portable IPs represent a significant strategic advantage for growing enterprises.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">IP Block Sizing & Efficiency</h3>
              <p>
                NEXX helps clients right-size their IP allocations to meet ARIN efficiency requirements (minimum 80% utilization within 12 months for most allocations). We provide subnet planning and VLSM (Variable Length Subnet Masking) design, growth projections and scalability analysis, documentation templates for ARIN submissions, and ongoing utilization tracking and reporting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-nexx-green to-green-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <Lock className="h-16 w-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-4xl lg:text-5xl font-black mb-6">
            Ready to Build Infrastructure That Scales?
          </h2>
          <p className="text-xl lg:text-2xl mb-8 opacity-95 leading-relaxed">
            Whether you need residential proxies for data intelligence, custom fiber connectivity for AI operations, or large IP blocks for your growing network, NEXX TELECOM delivers infrastructure solutions that actually work.
          </p>
          <p className="text-lg mb-10 opacity-90">
            No sales pitches. No upselling. Just honest technical conversations with engineers who understand your requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => window.location.href = 'https://nexx.net'}
              className="bg-white text-nexx-green hover:bg-gray-100 px-10 py-6 text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              Visit NEXX TELECOM
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.location.href = 'tel:+13054901669'}
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg font-bold"
            >
              <Phone className="mr-2 h-5 w-5" />
              +1 (305) 490-1669
            </Button>
          </div>
          <p className="mt-8 text-sm opacity-75">
            Based in Miami, Florida • Serving clients globally • 24/7/365 NOC Support
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NexxInnovate;